import type { CloudpackServer, Context } from '@ms-cloudpack/api-server';
import { cyan, bold } from '@ms-cloudpack/task-reporter';
import { getCertificate } from '@ms-cloudpack/setup-utilities';

/**
 * Create the API server, or return an error message if the server could not be created.
 */
export async function createApiServer(
  options: { port?: number | number[] },
  apiContext: Context,
): Promise<{ hasErrors: false; apiServer: CloudpackServer } | { hasErrors: true; message: string }> {
  const { port } = options;
  const { session } = apiContext;
  const { config } = session;
  const { server, appPath } = config;

  // If a cert is needed, check that it exists.
  if (server?.https === true && !(await getCertificate({ domain: server.domain, cwd: appPath }))) {
    return {
      hasErrors: true,
      message:
        `The local site is configured to use https in cloudpack.config.json, but is missing a cert.\n\n` +
        `Run "${cyan(bold('cloudpack setup'))}" to create the required certificate and try start again.\n`,
    };
  }

  const { createCloudpackServer } = await import('@ms-cloudpack/api-server');

  try {
    const apiServer = await createCloudpackServer({ port }, apiContext);
    return { hasErrors: false, apiServer };
  } catch (err) {
    let message: string;
    if ((err as { code: string }).code === 'EADDRNOTAVAIL' && server?.domain) {
      const primaryDomain = (Array.isArray(server.domain) ? server.domain : [server.domain])[0];
      message =
        `The domain "${cyan(bold(primaryDomain))}" in cloudpack.config.json is not resolvable.\n\n` +
        `Run "${cyan(bold('cloudpack setup'))}" to add the appropriate entries to your hosts file and ` +
        `try start again.\n`;
    } else {
      message = `Error creating API server: ${(err as Error).stack || err}`;
    }
    return { hasErrors: true, message };
  }
}
